import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import { getEmpresas, getPlanes, crearEmpresa, EmpresaResumen, PlanDto } from '../../services/adminService';
import './EmpresasPage.css';

const EmpresasPage: React.FC = () => {
  const [empresas, setEmpresas] = useState<EmpresaResumen[]>([]);
  const [planes, setPlanes] = useState<PlanDto[]>([]);
  const [busqueda, setBusqueda] = useState('');
  const [filtroActivas, setFiltroActivas] = useState<string>('todas');
  const [cargando, setCargando] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [resultado, setResultado] = useState<{ empresa: EmpresaResumen; password: string } | null>(null);
  const [form, setForm] = useState({ nombre: '', planCodigo: 'FREE', emailAdmin: '', nombreAdmin: '' });
  const [formError, setFormError] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);
  const navigate = useNavigate();

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const soloActivas = filtroActivas === 'activas' ? true : filtroActivas === 'inactivas' ? false : undefined;
      const [emp, pl] = await Promise.all([getEmpresas({ busqueda: busqueda || undefined, soloActivas }), getPlanes()]);
      setEmpresas(emp);
      setPlanes(pl);
    } catch { } finally {
      setCargando(false);
    }
  }, [busqueda, filtroActivas]);

  useEffect(() => { cargar(); }, [cargar]);

  const handleCrear = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setGuardando(true);
    try {
      const res = await crearEmpresa(form);
      setResultado({ empresa: res.empresa, password: res.passwordTemporal });
      setForm({ nombre: '', planCodigo: 'FREE', emailAdmin: '', nombreAdmin: '' });
      cargar();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Error al crear empresa');
    } finally {
      setGuardando(false);
    }
  };

  const cerrarModal = () => { setShowModal(false); setResultado(null); setFormError(null); };

  return (
    <AdminLayout>
      <div className="emp-page">
        <div className="emp-header">
          <div>
            <h1 className="emp-title">Empresas</h1>
            <p className="emp-subtitle">{empresas.length} empresa{empresas.length !== 1 ? 's' : ''} registrada{empresas.length !== 1 ? 's' : ''}</p>
          </div>
          <button className="emp-btn-primary" onClick={() => setShowModal(true)}>
            <i className="fas fa-plus"></i> Nueva empresa
          </button>
        </div>

        {/* Filtros */}
        <div className="emp-filters">
          <div className="emp-search">
            <i className="fas fa-search"></i>
            <input
              type="text"
              placeholder="Buscar empresa..."
              value={busqueda}
              onChange={e => setBusqueda(e.target.value)}
            />
          </div>
          <select value={filtroActivas} onChange={e => setFiltroActivas(e.target.value)} className="emp-select">
            <option value="todas">Todas</option>
            <option value="activas">Solo activas</option>
            <option value="inactivas">Solo inactivas</option>
          </select>
        </div>

        {/* Tabla */}
        {cargando ? (
          <div className="emp-loading"><i className="fas fa-spinner fa-spin"></i> Cargando...</div>
        ) : empresas.length === 0 ? (
          <div className="emp-empty"><i className="fas fa-building"></i><p>No hay empresas registradas</p></div>
        ) : (
          <div className="emp-table-wrapper">
            <table className="emp-table">
              <thead>
                <tr>
                  <th>Empresa</th>
                  <th>Plan</th>
                  <th>Empleados</th>
                  <th>Usuarios</th>
                  <th>Estado</th>
                  <th>Alta</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {empresas.map(e => (
                  <tr key={e.id}>
                    <td className="emp-td-name">{e.nombre}</td>
                    <td>
                      <span className="emp-plan-badge">{e.plan?.nombre ?? '—'}</span>
                    </td>
                    <td>{e.cantEmpleados} / {e.plan?.maxEmpleados ?? '?'}</td>
                    <td>{e.cantUsuarios}</td>
                    <td>
                      <span className={`emp-estado ${e.estaActiva ? 'activa' : 'inactiva'}`}>
                        {e.estaActiva ? 'Activa' : 'Inactiva'}
                      </span>
                    </td>
                    <td>{new Date(e.fechaAlta).toLocaleDateString('es-AR')}</td>
                    <td>
                      <button className="emp-btn-detail" onClick={() => navigate(`/admin/empresas/${e.id}`)}>
                        <i className="fas fa-chevron-right"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal crear empresa */}
      {showModal && (
        <div className="emp-modal-overlay" onClick={cerrarModal}>
          <div className="emp-modal" onClick={e => e.stopPropagation()}>
            {resultado ? (
              <>
                <div className="emp-modal-success">
                  <i className="fas fa-check-circle"></i>
                  <h2>Empresa creada</h2>
                  <p>La empresa <strong>{resultado.empresa.nombre}</strong> fue creada exitosamente.</p>
                </div>
                <div className="emp-password-box">
                  <p className="emp-password-label">Contraseña temporal del admin:</p>
                  <div className="emp-password-value">
                    <code>{resultado.password}</code>
                    <button onClick={() => navigator.clipboard.writeText(resultado.password)} title="Copiar">
                      <i className="fas fa-copy"></i>
                    </button>
                  </div>
                  <p className="emp-password-warning">
                    <i className="fas fa-exclamation-triangle"></i> Guardala ahora, no se mostrará de nuevo.
                  </p>
                </div>
                <button className="emp-btn-primary full" onClick={cerrarModal}>Cerrar</button>
              </>
            ) : (
              <>
                <h2 className="emp-modal-title">Nueva empresa</h2>
                <form onSubmit={handleCrear} className="emp-modal-form">
                  {formError && <div className="emp-form-error"><i className="fas fa-exclamation-circle"></i> {formError}</div>}

                  <div className="emp-field">
                    <label>Nombre de la empresa</label>
                    <input type="text" value={form.nombre} onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))} required placeholder="Ej: Acme S.A." />
                  </div>
                  <div className="emp-field">
                    <label>Plan</label>
                    <select value={form.planCodigo} onChange={e => setForm(f => ({ ...f, planCodigo: e.target.value }))}>
                      {planes.filter(p => p.activo).map(p => (
                        <option key={p.codigo} value={p.codigo}>{p.nombre} — ${p.precioUsdMes}/mes ({p.maxEmpleados} emp.)</option>
                      ))}
                    </select>
                  </div>
                  <div className="emp-field">
                    <label>Nombre del admin</label>
                    <input type="text" value={form.nombreAdmin} onChange={e => setForm(f => ({ ...f, nombreAdmin: e.target.value }))} required placeholder="Ej: Juan Pérez" />
                  </div>
                  <div className="emp-field">
                    <label>Email del admin</label>
                    <input type="email" value={form.emailAdmin} onChange={e => setForm(f => ({ ...f, emailAdmin: e.target.value }))} required placeholder="admin@empresa.com" />
                  </div>

                  <div className="emp-modal-actions">
                    <button type="button" className="emp-btn-secondary" onClick={cerrarModal}>Cancelar</button>
                    <button type="submit" className="emp-btn-primary" disabled={guardando}>
                      {guardando ? <><div className="emp-spinner"></div> Creando...</> : 'Crear empresa'}
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default EmpresasPage;
